/** The meters on one transformer, as a checklist, so a call-in or crew report
 *  can say which services were actually out (e.g. 2 of 6) instead of charging
 *  the whole transformer. `value` null means "all of them". */
import { useQuery } from '@tanstack/react-query'
import { getMeters } from '../../api/topology.ts'

export function MeterChecklist({
  transformerId,
  value,
  onChange,
}: {
  transformerId: number
  value: number[] | null
  onChange: (meterIds: number[] | null) => void
}) {
  const { data: meters, isLoading, isError } = useQuery({
    queryKey: ['meters', 'by-transformer', transformerId],
    queryFn: () => getMeters({ transformer_id: transformerId, limit: 1000 }),
  })

  if (isLoading) return <div className="text-xs text-gray-400">Loading meters…</div>
  if (isError) return <div className="text-xs text-red-600">Could not load this transformer’s meters.</div>
  const all = Array.isArray(meters) ? meters : []
  if (all.length === 0) return <div className="text-xs text-gray-400">No meters on this transformer.</div>

  const selected = new Set(value ?? all.map(m => m.id))
  function toggle(id: number) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    // every meter checked -> "all" (send nothing); none -> keep an empty list
    onChange(next.size === all.length ? null : [...next].sort((a, b) => a - b))
  }

  return (
    <div className="border border-gray-200 rounded-md max-h-40 overflow-auto divide-y divide-gray-50">
      <label className="flex items-center gap-2 px-2 py-1 text-xs bg-gray-50 sticky top-0">
        <input
          type="checkbox"
          checked={selected.size === all.length}
          onChange={() => onChange(selected.size === all.length ? [] : null)}
        />
        All {all.length} meters
      </label>
      {all.map(m => (
        <label key={m.id} className="flex items-center gap-2 px-2 py-1 text-xs">
          <input
            type="checkbox"
            checked={selected.has(m.id)}
            onChange={() => toggle(m.id)}
            aria-label={`Meter ${m.meter_number ?? m.id}`}
          />
          <span className="font-mono">{m.meter_number ?? `#${m.id}`}</span>
          {m.service_address && <span className="text-gray-500 truncate">{m.service_address}</span>}
        </label>
      ))}
    </div>
  )
}
